import React, { createContext, useState, useContext, useCallback, useEffect, useRef } from 'react';
import useWebSocket from '../hooks/useWebSocket';
import { useAuth } from './AuthContext';
import axios from 'axios'; // For initial fetch
import NotificationPopup from '../components/Shared/NotificationPopup';

const NotificationContext = createContext({
    notifications: [],
    unreadCount: 0,
    fetchNotifications: () => {}, // Keep if using initial fetch
    addNotification: (notification) => {}, // <--- Add function signature to default context
    markAsRead: (id) => {}, // Keep if manual marking is needed
    markAllAsRead: () => {}, // Keep if needed
});

export const useNotifications = () => useContext(NotificationContext);

export const NotificationProvider = ({ children }) => {
    const [notifications, setNotifications] = useState([]); // Store recent history (optional)
    const [unreadCount, setUnreadCount] = useState(0); // Maybe remove if not displaying count
    const [currentPopup, setCurrentPopup] = useState(null); // Holds the notification object for the *current* popup
    const popupTimeoutRef = useRef(null); // Ref to manage the auto-close timer

    const { isConnected, subscribe } = useWebSocket();
    const { isAuthenticated, token } = useAuth();
    const subscriptionRef = useRef(null);

    // --- Add Notification (Triggers Popup) ---
    // (Keep the existing addNotification function definition)
    const addNotification = useCallback((notification) => {
        // ... (implementation remains the same) ...
        const newNotification = {
            id: notification.id || `notif-${Date.now()}-${Math.random()}`,
            title: notification.title || 'Notification',
            message: notification.message || 'Something happened.',
            icon: notification.icon || 'fa-info-circle',
            color: notification.color || 'accent-blue',
            time: notification.time || new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }),
            read: notification.read || false,
        };
        console.log("Displaying notification popup:", newNotification);
        if (popupTimeoutRef.current) { clearTimeout(popupTimeoutRef.current); }
        setCurrentPopup(newNotification);
        popupTimeoutRef.current = setTimeout(() => {
            setCurrentPopup(null);
            popupTimeoutRef.current = null;
        }, 5000);
        setNotifications(prev => [newNotification, ...prev].slice(0, 20));
        if (!newNotification.read) { setUnreadCount(prev => prev + 1); }
    }, []);


    // Function to manually close the popup (e.g., on click)
    // (Keep the existing closePopup function definition)
    const closePopup = useCallback(() => {
        if (popupTimeoutRef.current) {
            clearTimeout(popupTimeoutRef.current);
            popupTimeoutRef.current = null;
        }
        setCurrentPopup(null);
    }, []);

    // WebSocket Subscription (Keep existing useEffect)
    useEffect(() => {
        // ... (subscription logic remains the same) ...
         if (isConnected && isAuthenticated) {
            console.log("Subscribing to general notifications topic (Example: /user/queue/notifications)...");
            if (subscriptionRef.current) subscriptionRef.current(); // Unsubscribe previous
            subscriptionRef.current = subscribe('/user/queue/notifications', (message) => {
                if (message) {
                    const errorMsg = message?.message || message?.error || "";
                    const planErrorMatch = errorMsg.match(/Insufficient plan\. Required: (.*?)(?: plan)?$/i);
                    if (!planErrorMatch) {
                        addNotification({
                            title: message.title || 'Server Update',
                            message: message.message || message.error || 'Received update.',
                            icon: message.icon || (message.error ? 'fa-exclamation-triangle' : 'fa-info-circle'),
                            color: message.color || (message.error ? 'accent-red' : 'accent-blue'),
                        });
                    } else {
                        console.log("Plan error received on general topic, ignoring for popup.");
                    }
                }
            });
            return () => {
                console.log("Unsubscribing from general notifications topic.");
                if (subscriptionRef.current) subscriptionRef.current();
                subscriptionRef.current = null;
            };
        } else {
            if (subscriptionRef.current) subscriptionRef.current();
            subscriptionRef.current = null;
            setCurrentPopup(null);
            if (popupTimeoutRef.current) clearTimeout(popupTimeoutRef.current);
        }
    }, [isConnected, isAuthenticated, subscribe, addNotification]);


    // Cleanup timeout on unmount (Keep existing useEffect)
    useEffect(() => {
        return () => {
             if (popupTimeoutRef.current) {
                 clearTimeout(popupTimeoutRef.current);
             }
         };
     }, []);


    // --- FIX HERE: Uncomment addNotification ---
    const contextValue = {
        addNotification, // <<< Uncomment this line
        notifications,
        unreadCount,
        // Keep other dummy/mock functions if needed by Topbar etc.
        fetchNotifications: () => {},
        markAsRead: () => {},
        markAllAsRead: () => {},
    };
    // --- END FIX ---

    return (
        <NotificationContext.Provider value={contextValue}>
            {children}
            {/* Render the popup component */}
            <NotificationPopup notification={currentPopup} onClose={closePopup} />
        </NotificationContext.Provider>
    );
};